import React, { useEffect, useState, useMemo } from 'react';
import * as XLSX from 'xlsx';
import apiService from '../services/api';

const Trades = ({ onGetUsersList, onGetTradingAccountList, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [activeTab, setActiveTab] = useState('positions'); // positions | orders | history | logs | status | currentloss
  const [loading, setLoading] = useState(false);
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [history, setHistory] = useState([]);
  const [historySymbolFilter, setHistorySymbolFilter] = useState('');
  const [historyRange, setHistoryRange] = useState('last5'); // last5 | custom
  const [historyStart, setHistoryStart] = useState('');
  const [historyEnd, setHistoryEnd] = useState('');
  const [logs, setLogs] = useState([]);
  const [symbolFilter, setSymbolFilter] = useState('');
  const [statusRows, setStatusRows] = useState([]);
  const [currentLossRows, setCurrentLossRows] = useState([]);
  const [currentLossSymbol, setCurrentLossSymbol] = useState('');
  const [accountAlias, setAccountAlias] = useState('');
  const [accountBalance, setAccountBalance] = useState(null);
  const [orderHistory, setOrderHistory] = useState([]);
  const [txnHistory, setTxnHistory] = useState([]);
  const [fundingRows, setFundingRows] = useState([]);
  const [txnSymbolFilter, setTxnSymbolFilter] = useState('');

  const filteredHistory = useMemo(() => {
    if (!historySymbolFilter) return history;
    const q = historySymbolFilter.toUpperCase();
    return history.filter(h => (h.symbol || '').toUpperCase().includes(q));
  }, [history, historySymbolFilter]);

  const exportRowsToXlsx = (rows, { fileName, sheetName }) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName || 'Sheet1');
    const data = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'export.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExportTradeHistory = () => {
    if (!filteredHistory.length) return;
    const safe = (s) => String(s ?? '').replace(/[\\/:*?"<>|]+/g, '-').trim();
    const today = new Date();
    const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const accountPart = safe(selectedAccount?.accLoginName || selectedAccount?.accountId || 'account');
    const symbolPart = safe(historySymbolFilter || 'ALL');
    const rangePart = safe(historyRange || '');
    const fileName = `trade-history_${accountPart}_${symbolPart}${rangePart ? `_${rangePart}` : ''}_${ymd}.xlsx`;

    exportRowsToXlsx(
      filteredHistory.map((h) => ({
        Time: h.tradeTime ? new Date(h.tradeTime).toLocaleString() : (h.time || ''),
        Symbol: h.symbol ?? '',
        Type: h.tradeType ?? '',
        Volume: h.volume ?? '',
        Entry: h.entryPrice ?? '',
        PnL: h.pnl ?? '',
      })),
      { fileName, sheetName: 'Trade History' }
    );
  };

  useEffect(() => {
    const init = async () => {
      const u = await onGetUsersList();
      if (u?.success) setUsers(u.data);
      const uid = currentUser?.userId || '';
      setSelectedUserId(uid);
      if (uid) {
        const acc = await onGetTradingAccountList({ userId: uid });
        if (acc?.success) setAccounts(acc.data);
      }
    };
    init();
  }, [onGetUsersList, onGetTradingAccountList, currentUser]);

  useEffect(() => {
    if (!selectedUserId) return;
    (async () => {
      const acc = await onGetTradingAccountList({ userId: selectedUserId });
      if (acc?.success) setAccounts(acc.data);
      setSelectedAccountId('');
      setSelectedAccount(null);
      setPositions([]);
      setOrders([]);
      setActiveTab('positions');
    })();
  }, [selectedUserId, onGetTradingAccountList]);

  const accountObj = useMemo(() => accounts.find(a => String(a.accountId) === String(selectedAccountId)), [accounts, selectedAccountId]);

  useEffect(() => {
    setSelectedAccount(accountObj || null);
  }, [accountObj]);

  // Reset displayed balance/alias when account changes
  useEffect(() => {
    setAccountAlias('');
    setAccountBalance(null);
  }, [selectedAccount]);

  const loadPositions = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const res = await apiService.getOpenPositions(selectedAccount);
      if (Array.isArray(res)) setPositions(res);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckBalance = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const res = await apiService.getAccountInfo({ binanceFuturesApiKey: selectedAccount.binanceFuturesApiKey, binanceFuturesSecretKey: selectedAccount.binanceFuturesSecretKey });
      if (res && res.errorCode === 200) {
        if (typeof res.accLoginName === 'string') setAccountAlias(res.accLoginName);
        if (typeof res.initialDeposit === 'number') setAccountBalance(res.initialDeposit);
      }
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const res = await apiService.getOpenOrders(selectedAccount);
      if (Array.isArray(res)) setOrders(res);
    } finally {
      setLoading(false);
    }
  };

  const loadOrderHistory = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const res = await apiService.getOrderHistory(selectedAccount);
      if (Array.isArray(res)) setOrderHistory(res);
    } finally { setLoading(false); }
  };

  const loadTransactionHistory = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const res = await apiService.getTransactionHistory(selectedAccount);
      if (Array.isArray(res)) setTxnHistory(res);
    } finally { setLoading(false); }
  };

  const loadFunding = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const now = Date.now();
      const res = await apiService.getFundingFeeHistory({ accountId: selectedAccount.accountId, startTimeMs: now - 7*24*60*60*1000, endTimeMs: now });
      if (Array.isArray(res)) setFundingRows(res);
    } finally { setLoading(false); }
  };

  const loadHistory = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      let startTimeMs;
      let endTimeMs;
      if (historyRange === 'last5') {
        const now = Date.now();
        endTimeMs = now;
        startTimeMs = now - 5 * 24 * 60 * 60 * 1000; // last 5 days
      } else if (historyRange === 'custom') {
        if (historyStart) startTimeMs = new Date(historyStart).getTime();
        if (historyEnd) endTimeMs = new Date(historyEnd).getTime();
      }
      const res = await apiService.getPositionHistory({ accountId: selectedAccount.accountId, startTimeMs, endTimeMs, binanceFuturesApiKey: selectedAccount.binanceFuturesApiKey, binanceFuturesSecretKey: selectedAccount.binanceFuturesSecretKey });
      if (Array.isArray(res)) setHistory(res);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    if (!selectedUserId) return;
    setLoading(true);
    try {
      const res = await apiService.getTradeLogs({ userId: Number(selectedUserId) || 0, accountId: Number(selectedAccountId) || 0, symbolName: symbolFilter || undefined });
      if (Array.isArray(res)) setLogs(res);
    } finally {
      setLoading(false);
    }
  };

  const loadStatus = async () => {
    if (!selectedUserId) return;
    setLoading(true);
    try {
      const res = await apiService.getPositionsStatus(Number(selectedUserId));
      if (Array.isArray(res)) setStatusRows(res);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentLoss = async () => {
    if (!selectedUserId) return;
    setLoading(true);
    try {
      const res = await apiService.getCurrentLoss(Number(selectedUserId));
      if (Array.isArray(res)) setCurrentLossRows(res);
    } finally {
      setLoading(false);
    }
  };

  const handleClosePosition = async (p) => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const qty = Math.abs(p.positionAmt);
      await apiService.closePosition({
        symbol: p.symbol,
        positionSide: p.positionSide,
        quantity: qty,
        binanceFuturesApiKey: selectedAccount.binanceFuturesApiKey,
        binanceFuturesSecretKey: selectedAccount.binanceFuturesSecretKey,
      });
      await loadPositions();
    } finally {
      setLoading(false);
    }
  };

  const handleCloseAllPositions = async () => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      await apiService.closeAllPositions({
        binanceFuturesApiKey: selectedAccount.binanceFuturesApiKey,
        binanceFuturesSecretKey: selectedAccount.binanceFuturesSecretKey,
      });
      await loadPositions();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAccount) {
      loadPositions();
    }
  }, [selectedAccount]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold text-white">Trades</h3>
          <p className="text-dark-400 text-sm mt-1">View live positions and open orders</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-dark-300 text-sm font-medium">User:</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm w-full sm:w-auto sm:min-w-[200px]"
            >
              <option value="">Select a user...</option>
              {users.map(u => (
                <option key={u.userId} value={u.userId}>{u.userFirstName} {u.userLastName} ({u.eMailId})</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <label className="text-dark-300 text-sm font-medium">Trading Account:</label>
            <select
              value={selectedAccountId}
              onChange={(e) => { setSelectedAccountId(e.target.value); setActiveTab('positions'); }}
              className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white focus:outline-none focus:border-primary-500 text-sm w-full sm:w-auto sm:min-w-[240px]"
            >
              <option value="">Select an account...</option>
              {accounts.map(a => (
                <option key={a.accountId} value={a.accountId}>{a.accLoginName} - {a.shortDescription || 'No description'}</option>
              ))}
            </select>
          </div>
          {selectedAccount && (
            <button onClick={loadPositions} className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600">Refresh</button>
          )}
          {selectedAccount && (
            <div className="flex items-center gap-2">
              <button onClick={handleCheckBalance} className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600">Check Balance</button>
              {(accountAlias || accountBalance !== null) && (
                <span className="text-sm text-dark-300">
                  {accountAlias ? `${accountAlias}` : ''}{accountAlias && accountBalance !== null ? ' • ' : ''}{accountBalance !== null ? `Available: ${accountBalance}` : ''}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {!selectedAccount ? (
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-6 text-dark-300">Select a user and trading account to view trades.</div>
      ) : (
        <div className="bg-dark-800 border border-dark-700 rounded-xl p-4 sm:p-6">
          <div className="flex gap-3 border-b border-dark-700 mb-4 overflow-x-auto">
            <button onClick={() => setActiveTab('positions')} className={`px-3 py-2 rounded-t ${activeTab==='positions'?'bg-dark-700 text-white':'text-dark-300 hover:text-white'}`}>Positions</button>
            <button onClick={() => { setActiveTab('orders'); loadOrders(); }} className={`px-3 py-2 rounded-t ${activeTab==='orders'?'bg-dark-700 text-white':'text-dark-300 hover:text-white'}`}>Orders</button>
            <button onClick={() => { setActiveTab('orderHistory'); loadOrderHistory(); }} className={`px-3 py-2 rounded-t ${activeTab==='orderHistory'?'bg-dark-700 text-white':'text-dark-300 hover:text-white'}`}>Order History</button>
            <button onClick={() => { setActiveTab('history'); loadHistory(); }} className={`px-3 py-2 rounded-t ${activeTab==='history'?'bg-dark-700 text-white':'text-dark-300 hover:text-white'}`}>Trade History</button>
            <button onClick={() => { setActiveTab('transactions'); loadTransactionHistory(); }} className={`px-3 py-2 rounded-t ${activeTab==='transactions'?'bg-dark-700 text-white':'text-dark-300 hover:text-white'}`}>Transaction History</button>
            <button onClick={() => { setActiveTab('funding'); loadFunding(); }} className={`px-3 py-2 rounded-t ${activeTab==='funding'?'bg-dark-700 text-white':'text-dark-300 hover:text-white'}`}>Funding Fee</button>
            <button onClick={() => { setActiveTab('logs'); loadLogs(); }} className={`px-3 py-2 rounded-t ${activeTab==='logs'?'bg-dark-700 text-white':'text-dark-300 hover:text-white'}`}>Trade Logs</button>
            <button onClick={() => { setActiveTab('status'); loadStatus(); }} className={`px-3 py-2 rounded-t ${activeTab==='status'?'bg-dark-700 text-white':'text-dark-300 hover:text-white'}`}>Status</button>
            <button onClick={() => { setActiveTab('currentloss'); loadCurrentLoss(); }} className={`px-3 py-2 rounded-t ${activeTab==='currentloss'?'bg-dark-700 text-white':'text-dark-300 hover:text-white'}`}>Current Loss</button>
          </div>
          {activeTab === 'orderHistory' && (
            <div>
              {loading ? (
                <div className="p-8 text-center text-dark-400">Loading order history...</div>
              ) : orderHistory.length === 0 ? (
                <div className="p-8 text-center text-dark-400">No data</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Time</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Side</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Type</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Price</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Orig Qty</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Executed</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orderHistory.map((o, idx) => (
                        <tr key={`${o.symbol}-${idx}`} className="border-b border-dark-700">
                          <td className="px-4 py-3 text-sm text-dark-300">{o.time ? new Date(o.time).toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-white">{o.symbol}</td>
                          <td className={`px-4 py-3 text-sm ${o.side==='BUY'?'text-green-400':'text-red-400'}`}>{o.side}</td>
                          <td className="px-4 py-3 text-sm text-white">{o.type}</td>
                          <td className="px-4 py-3 text-sm text-white">{o.price}</td>
                          <td className="px-4 py-3 text-sm text-white">{o.origQty}</td>
                          <td className="px-4 py-3 text-sm text-white">{o.executedQty}</td>
                          <td className="px-4 py-3 text-sm text-white">{o.status}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'transactions' && (
            <div>
              {loading ? (
                <div className="p-8 text-center text-dark-400">Loading transactions...</div>
              ) : txnHistory.length === 0 ? (
                <div className="p-8 text-center text-dark-400">No data</div>
              ) : (
                <div className="overflow-x-auto">
                  <div className="flex flex-wrap items-end gap-3 mb-3">
                    <div className="flex items-center gap-2">
                      <label className="text-dark-300 text-sm font-medium">Symbol:</label>
                      <input value={txnSymbolFilter} onChange={(e)=>setTxnSymbolFilter(e.target.value)} placeholder="e.g. BTCUSDT" className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm" />
                    </div>
                    <button onClick={loadTransactionHistory} className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600">Refresh</button>
                    <button onClick={()=>setTxnSymbolFilter('')} className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600">Clear</button>
                    {(() => {
                      const filtered = (txnSymbolFilter ? txnHistory.filter(t => (t.symbol||'').toUpperCase().includes(txnSymbolFilter.toUpperCase())) : txnHistory);
                      const totalAmount = filtered.reduce((s, t) => s + (Number(t.income) || 0), 0);
                      return (
                        <div className="px-3 py-2 rounded bg-dark-700 text-sm">
                          <span className="text-dark-300">Total Amount: </span>
                          <span className={`${totalAmount>=0?'text-green-400':'text-red-400'}`}>{totalAmount.toFixed(4)}</span>
                        </div>
                      );
                    })()}
                  </div>
                  <table className="w-full">
                    <thead className="bg-dark-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Time</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Type</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Asset</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Info</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(txnSymbolFilter ? txnHistory.filter(t => (t.symbol||'').toUpperCase().includes(txnSymbolFilter.toUpperCase())) : txnHistory).map((t, idx) => (
                        <tr key={`${t.tranId || idx}`} className="border-b border-dark-700">
                          <td className="px-4 py-3 text-sm text-dark-300">{t.time ? new Date(t.time).toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-white">{t.symbol}</td>
                          <td className="px-4 py-3 text-sm text-white">{t.incomeType}</td>
                          <td className="px-4 py-3 text-sm text-white">{t.asset}</td>
                          <td className="px-4 py-3 text-sm text-white">{t.info}</td>
                          <td className={`px-4 py-3 text-sm ${t.income>=0?'text-green-400':'text-red-400'}`}>{t.income}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'funding' && (
            <div>
              {loading ? (
                <div className="p-8 text-center text-dark-400">Loading funding fees...</div>
              ) : fundingRows.length === 0 ? (
                <div className="p-8 text-center text-dark-400">No data</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Time</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Type</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">PnL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fundingRows.map((r, idx) => (
                        <tr key={`${r.symbol}-${idx}`} className="border-b border-dark-700">
                          <td className="px-4 py-3 text-sm text-dark-300">{r.tradeTime ? new Date(r.tradeTime).toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-white">{r.symbol}</td>
                          <td className="px-4 py-3 text-sm text-white">{r.tradeType}</td>
                          <td className={`px-4 py-3 text-sm ${r.pnl>=0?'text-green-400':'text-red-400'}`}>{r.pnl}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'positions' && (
            <div>
              <div className="flex justify-end mb-3">
                <button onClick={handleCloseAllPositions} disabled={loading || positions.length===0} className="bg-red-600 text-white px-3 py-2 rounded text-sm disabled:opacity-50">Close All</button>
              </div>
              {loading ? (
                <div className="p-8 text-center text-dark-400">Loading positions...</div>
              ) : positions.length === 0 ? (
                <div className="p-8 text-center text-dark-400">No open positions</div>
              ) : (
                <>
                  <div className="sm:hidden space-y-3">
                    {positions.map((p, idx) => (
                      <div key={`${p.symbol}-m-${idx}`} className="bg-dark-700 border border-dark-600 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <div className="text-white font-semibold">{p.symbol}</div>
                          <div className={`${p.positionSide==='LONG'?'text-green-400':'text-red-400'} text-sm font-semibold`}>{p.positionSide}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="text-dark-300">Qty</div><div className="text-white text-right">{p.positionAmt}</div>
                          <div className="text-dark-300">Entry</div><div className="text-white text-right">{p.entryPrice}</div>
                          <div className="text-dark-300">Lev</div><div className="text-white text-right">{p.leverage}x</div>
                          <div className="text-dark-300">PnL</div><div className={`${p.unrealizedProfit>=0?'text-green-400':'text-red-400'} text-right`}>{p.unrealizedProfit}</div>
                        </div>
                        <div className="flex justify-between items-center mt-2">
                          <div className="text-xs text-dark-400">{p.updateTime ? new Date(p.updateTime).toLocaleString() : '-'}</div>
                          <button onClick={() => handleClosePosition(p)} className="bg-red-700 text-white px-2 py-1 rounded text-xs">Close</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-dark-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Side</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Qty</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Entry</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Leverage</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Unrealized PnL</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Updated</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {positions.map((p, idx) => (
                          <tr key={`${p.symbol}-${idx}`} className="border-b border-dark-700">
                            <td className="px-4 py-3 text-sm text-white">{p.symbol}</td>
                            <td className={`px-4 py-3 text-sm ${p.positionSide==='LONG'?'text-green-400':'text-red-400'}`}>{p.positionSide}</td>
                            <td className="px-4 py-3 text-sm text-white">{p.positionAmt}</td>
                            <td className="px-4 py-3 text-sm text-white">{p.entryPrice}</td>
                            <td className="px-4 py-3 text-sm text-white">{p.leverage}x</td>
                            <td className={`px-4 py-3 text-sm ${p.unrealizedProfit>=0?'text-green-400':'text-red-400'}`}>{p.unrealizedProfit}</td>
                            <td className="px-4 py-3 text-sm text-dark-300">{p.updateTime ? new Date(p.updateTime).toLocaleString() : '-'}</td>
                            <td className="px-4 py-3 text-sm">
                              <button onClick={() => handleClosePosition(p)} className="bg-red-700 text-white px-2 py-1 rounded text-xs">Close</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'logs' && (
            <div>
              {loading ? (
                <div className="p-8 text-center text-dark-400">Loading logs...</div>
              ) : logs.length === 0 ? (
                <div className="p-8 text-center text-dark-400">No logs</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Open Time</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Side</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Entry</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Exit</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Leverage</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">TP1 Size</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">TP1 %</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">TP2 Size</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">TP2 %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((l) => (
                        <tr key={l.tradeLogId} className="border-b border-dark-700">
                          <td className="px-4 py-3 text-sm text-dark-300">{l.openTime ? new Date(l.openTime).toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-white">{l.symbol}</td>
                          <td className={`px-4 py-3 text-sm ${l.longOrShort===1?'text-green-400': 'text-red-400'}`}>{l.longOrShort===1?'LONG':'SHORT'}</td>
                          <td className="px-4 py-3 text-sm text-white">{l.entryPrice}</td>
                          <td className="px-4 py-3 text-sm text-white">{l.exitPrice ?? '-'}</td>
                          <td className="px-4 py-3 text-sm text-white">{l.leverage}x</td>
                          <td className="px-4 py-3 text-sm text-white">{l.tp1Size}</td>
                          <td className="px-4 py-3 text-sm text-white">{(l.tp1Percent ?? 0).toFixed(2)}%</td>
                          <td className="px-4 py-3 text-sm text-white">{l.tp2Size}</td>
                          <td className="px-4 py-3 text-sm text-white">{(l.tp2Percent ?? 0).toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'status' && (
            <div>
              {loading ? (
                <div className="p-8 text-center text-dark-400">Loading status...</div>
              ) : statusRows.length === 0 ? (
                <div className="p-8 text-center text-dark-400">No data</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Side</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Entry</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Exit</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Size (USDT)</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">TP</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">SL</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Open</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Leverage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusRows.map((r, idx) => (
                        <tr key={`${r.symbol}-${idx}`} className="border-b border-dark-700">
                          <td className="px-4 py-3 text-sm text-white">{r.symbol}</td>
                          <td className={`px-4 py-3 text-sm ${r.longOrShort===1?'text-green-400':'text-red-400'}`}>{r.longOrShort===1?'LONG':'SHORT'}</td>
                          <td className="px-4 py-3 text-sm text-white">{r.entryPrice}</td>
                          <td className="px-4 py-3 text-sm text-white">{r.exitPrice ?? '-'}</td>
                          <td className="px-4 py-3 text-sm text-white">{r.sizeUsdt}</td>
                          <td className="px-4 py-3 text-sm text-white">{r.takeProfit}</td>
                          <td className="px-4 py-3 text-sm text-white">{r.stopLoss}</td>
                          <td className="px-4 py-3 text-sm text-white">{r.positionOpen ? 'Yes' : 'No'}</td>
                          <td className="px-4 py-3 text-sm text-white">{r.leverage}x</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {activeTab === 'currentloss' && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <label className="text-dark-300 text-sm font-medium">Symbol:</label>
                <input value={currentLossSymbol} onChange={(e)=>setCurrentLossSymbol(e.target.value)} placeholder="e.g. BTCUSDT" className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm" />
                <button onClick={loadCurrentLoss} className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600">Refresh</button>
              </div>
              {loading ? (
                <div className="p-8 text-center text-dark-400">Loading current loss...</div>
              ) : (currentLossRows.filter(r => !currentLossSymbol || (r.symbol||'').toUpperCase().includes(currentLossSymbol.toUpperCase()))).length === 0 ? (
                <div className="p-8 text-center text-dark-400">No data</div>
              ) : (
                <div>
                  {(() => {
                    const filtered = currentLossRows.filter(r => !currentLossSymbol || (r.symbol||'').toUpperCase().includes(currentLossSymbol.toUpperCase()));
                    const totalFiltered = filtered.reduce((s, r) => s + (Number(r.pnl) || 0), 0);
                    return (
                      <div className="px-3 py-2 rounded bg-dark-700 text-sm mb-3 inline-block">
                        <span className="text-dark-300">Total PnL: </span>
                        <span className={`${totalFiltered>=0?'text-green-400':'text-red-400'}`}>{totalFiltered.toFixed(4)}</span>
                      </div>
                    );
                  })()}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-dark-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Time</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Type</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Volume</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Entry</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">PNL</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentLossRows.filter(r => !currentLossSymbol || (r.symbol||'').toUpperCase().includes(currentLossSymbol.toUpperCase())).map((r, idx) => (
                          <tr key={`${r.currentLossId}-${idx}`} className="border-b border-dark-700">
                            <td className="px-4 py-3 text-sm text-dark-300">{r.tradeTime ? new Date(r.tradeTime).toLocaleString() : '-'}</td>
                            <td className="px-4 py-3 text-sm text-white">{r.symbol}</td>
                            <td className={`px-4 py-3 text-sm ${r.tradeType==='BUY'?'text-green-400':'text-red-400'}`}>{r.tradeType}</td>
                            <td className="px-4 py-3 text-sm text-white">{r.volume}</td>
                            <td className="px-4 py-3 text-sm text-white">{r.entryPrice}</td>
                            <td className={`px-4 py-3 text-sm ${r.pnl>=0?'text-green-400':'text-red-400'}`}>{r.pnl}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'orders' && (
            <div>
              {loading ? (
                <div className="p-8 text-center text-dark-400">Loading orders...</div>
              ) : orders.length === 0 ? (
                <div className="p-8 text-center text-dark-400">No open orders</div>
              ) : (
                <>
                  <div className="sm:hidden space-y-3">
                    {orders.map((o, idx) => (
                      <div key={`${o.symbol}-o-${idx}`} className="bg-dark-700 border border-dark-600 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <div className="text-white font-semibold">{o.symbol}</div>
                          <div className={`${o.side==='BUY'?'text-green-400':'text-red-400'} text-sm font-semibold`}>{o.side}</div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div className="text-dark-300">Type</div><div className="text-white text-right">{o.type}</div>
                          <div className="text-dark-300">Price</div><div className="text-white text-right">{o.price}</div>
                          <div className="text-dark-300">Qty</div><div className="text-white text-right">{o.origQty}</div>
                          <div className="text-dark-300">Status</div><div className="text-white text-right">{o.status}</div>
                        </div>
                        <div className="text-xs text-dark-400 mt-2 text-right">{o.time ? new Date(o.time).toLocaleString() : '-'}</div>
                      </div>
                    ))}
                  </div>
                  <div className="hidden sm:block overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-dark-700">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Side</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Type</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Price</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Quantity</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Status</th>
                          <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((o, idx) => (
                          <tr key={`${o.symbol}-${idx}`} className="border-b border-dark-700">
                            <td className="px-4 py-3 text-sm text-white">{o.symbol}</td>
                            <td className={`px-4 py-3 text-sm ${o.side==='BUY'?'text-green-400':'text-red-400'}`}>{o.side}</td>
                            <td className="px-4 py-3 text-sm text-white">{o.type}</td>
                            <td className="px-4 py-3 text-sm text-white">{o.price}</td>
                            <td className="px-4 py-3 text-sm text-white">{o.origQty}</td>
                            <td className="px-4 py-3 text-sm text-white">{o.status}</td>
                            <td className="px-4 py-3 text-sm text-dark-300">{o.time ? new Date(o.time).toLocaleString() : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div>
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <label className="text-dark-300 text-sm font-medium">Symbol:</label>
                  <input value={historySymbolFilter} onChange={(e) => setHistorySymbolFilter(e.target.value)} placeholder="e.g. BTCUSDT" className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-dark-300 text-sm font-medium">Range:</label>
                  <label className="flex items-center gap-1 text-sm text-dark-200">
                    <input type="radio" name="histRange" checked={historyRange==='last5'} onChange={() => setHistoryRange('last5')} /> Last 5 days
                  </label>
                  <label className="flex items-center gap-1 text-sm text-dark-200">
                    <input type="radio" name="histRange" checked={historyRange==='custom'} onChange={() => setHistoryRange('custom')} /> Custom
                  </label>
                </div>
                {historyRange==='custom' && (
                  <>
                    <div className="flex items-center gap-2">
                      <label className="text-dark-300 text-sm font-medium">Start:</label>
                      <input type="date" value={historyStart} onChange={e=>setHistoryStart(e.target.value)} className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm" />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-dark-300 text-sm font-medium">End:</label>
                      <input type="date" value={historyEnd} onChange={e=>setHistoryEnd(e.target.value)} className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm" />
                    </div>
                  </>
                )}
                <button onClick={loadHistory} className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600">Refresh</button>
                <button onClick={() => setHistorySymbolFilter('')} className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600">Clear</button>
                <button
                  onClick={handleExportTradeHistory}
                  disabled={loading || filteredHistory.length === 0}
                  className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600 disabled:opacity-50"
                  title={filteredHistory.length === 0 ? 'No filtered rows to export' : 'Download filtered data as .xlsx'}
                >
                  Export to Excel
                </button>
              </div>
              {loading ? (
                <div className="p-8 text-center text-dark-400">Loading history...</div>
              ) : history.length === 0 ? (
                <div className="p-8 text-center text-dark-400">No recent trades</div>
              ) : (
                <div>
                  {(() => {
                    const totalAll = history.reduce((s, h) => s + (Number(h.pnl) || 0), 0);
                    const totalFiltered = filteredHistory.reduce((s, h) => s + (Number(h.pnl) || 0), 0);
                    return (
                      <div className="flex flex-wrap gap-4 mb-3">
                        <div className={`px-3 py-2 rounded bg-dark-700 text-sm`}>
                          <span className="text-dark-300">Total PnL (All): </span>
                          <span className={`${totalAll>=0?'text-green-400':'text-red-400'}`}>{totalAll.toFixed(4)}</span>
                        </div>
                        <div className={`px-3 py-2 rounded bg-dark-700 text-sm`}>
                          <span className="text-dark-300">Total PnL (Filtered): </span>
                          <span className={`${totalFiltered>=0?'text-green-400':'text-red-400'}`}>{totalFiltered.toFixed(4)}</span>
                        </div>
                      </div>
                    );
                  })()}
                  <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Time</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Type</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Volume</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Entry</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">PnL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredHistory.map((h, idx) => (
                        <tr key={`${h.tradeId || idx}`} className="border-b border-dark-700">
                          <td className="px-4 py-3 text-sm text-dark-300">{h.tradeTime ? new Date(h.tradeTime).toLocaleString() : h.time || '-'}</td>
                          <td className="px-4 py-3 text-sm text-white">{h.symbol}</td>
                          <td className={`px-4 py-3 text-sm ${h.tradeType==='BUY'?'text-green-400': h.tradeType==='SELL'?'text-red-400':'text-dark-200'}`}>{h.tradeType}</td>
                          <td className="px-4 py-3 text-sm text-white">{h.volume}</td>
                          <td className="px-4 py-3 text-sm text-white">{h.entryPrice}</td>
                          <td className={`px-4 py-3 text-sm ${h.pnl>=0?'text-green-400':'text-red-400'}`}>{h.pnl}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'logs' && (
            <div>
              <div className="flex flex-wrap items-end gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <label className="text-dark-300 text-sm font-medium">Symbol:</label>
                  <input value={symbolFilter} onChange={(e) => setSymbolFilter(e.target.value)} placeholder="e.g. BTCUSDT" className="px-3 py-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm" />
                </div>
                <button onClick={loadLogs} className="px-3 py-2 bg-dark-700 text-dark-200 rounded-lg text-sm hover:bg-dark-600">Apply</button>
              </div>
              {loading ? (
                <div className="p-8 text-center text-dark-400">Loading logs...</div>
              ) : logs.length === 0 ? (
                <div className="p-8 text-center text-dark-400">No logs</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-dark-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Open Time</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Symbol</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Side</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Entry</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Exit</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">Leverage</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">TP1 Size</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">TP1 %</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">TP2 Size</th>
                        <th className="px-4 py-3 text-left text-xs sm:text-sm font-semibold text-white">TP2 %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((l) => (
                        <tr key={l.tradeLogId} className="border-b border-dark-700">
                          <td className="px-4 py-3 text-sm text-dark-300">{l.openTime ? new Date(l.openTime).toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-white">{l.symbol}</td>
                          <td className={`px-4 py-3 text-sm ${l.longOrShort===1?'text-green-400': 'text-red-400'}`}>{l.longOrShort===1?'LONG':'SHORT'}</td>
                          <td className="px-4 py-3 text-sm text-white">{l.entryPrice}</td>
                          <td className="px-4 py-3 text-sm text-white">{l.exitPrice ?? '-'}</td>
                          <td className="px-4 py-3 text-sm text-white">{l.leverage}x</td>
                          <td className="px-4 py-3 text-sm text-white">{l.tp1Size}</td>
                          <td className="px-4 py-3 text-sm text-white">{(l.tp1Percent ?? 0).toFixed(2)}%</td>
                          <td className="px-4 py-3 text-sm text-white">{l.tp2Size}</td>
                          <td className="px-4 py-3 text-sm text-white">{(l.tp2Percent ?? 0).toFixed(2)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Trades;


